/**
 * Barcode symbol encoding.
 *
 * Written out rather than pulled from a package: it is a few tables and a checksum, it must work
 * offline on a shop's till, and a scanner is unforgiving — a barcode that is one module wide in
 * the wrong place is not a barcode, it is ink. Encoding it here means the same code is on screen,
 * on the label and in the tests.
 *
 * Both encoders return a string of "1" and "0", one character per module (the narrowest bar).
 * Rendering is separate, so the same pattern drives an SVG, a canvas or anything else.
 */

/* --------------------------------------------------------------------- EAN-13 */

/** Left-hand odd parity. */
const L = [
  '0001101', '0011001', '0010011', '0111101', '0100011',
  '0110001', '0101111', '0111011', '0110111', '0001011',
]

/** Left-hand even parity. */
const G = [
  '0100111', '0110011', '0011011', '0100001', '0011101',
  '0111001', '0000101', '0010001', '0001001', '0010111',
]

/** Right-hand, always even parity. */
const R = [
  '1110010', '1100110', '1101100', '1000010', '1011100',
  '1001110', '1010000', '1000100', '1001000', '1110100',
]

/**
 * Which of the first six digits use even parity, chosen by the first digit.
 *
 * This is how thirteen digits fit into twelve symbols: the first digit is never drawn, it is
 * encoded in the parity pattern of the six that follow.
 */
const PARITY = [
  'LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG',
  'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL',
]

export function ean13CheckDigit(first12: string): number {
  let sum = 0
  for (let index = 0; index < 12; index++) {
    const digit = first12.charCodeAt(index) - 48
    sum += index % 2 === 0 ? digit : digit * 3
  }
  return (10 - (sum % 10)) % 10
}

export function isValidEan13(code: string): boolean {
  return (
    /^\d{13}$/.test(code) && ean13CheckDigit(code.slice(0, 12)) === code.charCodeAt(12) - 48
  )
}

/** Encodes an EAN-13 into 95 modules: guard, six, centre, six, guard. */
export function encodeEan13(code: string): string {
  if (!isValidEan13(code)) {
    throw new Error(`${code} is not a valid EAN-13.`)
  }

  const digits = [...code].map((character) => character.charCodeAt(0) - 48)
  const parity = PARITY[digits[0]!]!

  let modules = '101'
  for (let index = 1; index <= 6; index++) {
    modules += parity[index - 1] === 'L' ? L[digits[index]!]! : G[digits[index]!]!
  }
  modules += '01010'
  for (let index = 7; index <= 12; index++) {
    modules += R[digits[index]!]!
  }
  return modules + '101'
}

/* -------------------------------------------------------------------- Code 128 */

/**
 * Code 128 set B patterns, indexed by symbol value.
 *
 * Set B covers upper and lower case, digits and punctuation, which is everything a shop is
 * realistically going to type into a barcode field.
 */
const CODE128 = [
  '11011001100', '11001101100', '11001100110', '10010011000', '10010001100',
  '10001001100', '10011001000', '10011000100', '10001100100', '11001001000',
  '11001000100', '11000100100', '10110011100', '10011011100', '10011001110',
  '10111001100', '10011101100', '10011100110', '11001110010', '11001011100',
  '11001001110', '11011100100', '11001110100', '11101101110', '11101001100',
  '11100101100', '11100100110', '11101100100', '11100110100', '11100110010',
  '11011011000', '11011000110', '11000110110', '10100011000', '10001011000',
  '10001000110', '10110001000', '10001101000', '10001100010', '11010001000',
  '11000101000', '11000100010', '10110111000', '10110001110', '10001101110',
  '10111011000', '10111000110', '10001110110', '11101110110', '11010001110',
  '11000101110', '11011101000', '11011100010', '11011101110', '11101011000',
  '11101000110', '11100010110', '11101101000', '11101100010', '11100011010',
  '11101111010', '11001000010', '11110001010', '10100110000', '10100001100',
  '10010110000', '10010000110', '10000101100', '10000100110', '10110010000',
  '10110000100', '10011010000', '10011000010', '10000110100', '10000110010',
  '11000010010', '11001010000', '11110111010', '11000010100', '10001111010',
  '10100111100', '10010111100', '10010011110', '10111100100', '10011110100',
  '10011110010', '11110100100', '11110010100', '11110010010', '11011011110',
  '11011110110', '11110110110', '10101111000', '10100011110', '10001011110',
  '10111101000', '10111100010', '11110101000', '11110100010', '10111011110',
  '10111101110', '11101011110', '11110101110', '11010000100', '11010010000',
  '11010011100', '11000111010',
]

const CODE128_STOP = '1100011101011'
const START_B = 104

/**
 * Encodes any printable ASCII into Code 128 set B.
 *
 * The checksum is a weighted sum of the symbol values, modulo 103. Omitting it produces a symbol
 * that looks perfectly plausible and that no scanner will read.
 */
export function encodeCode128(value: string): string {
  const values: number[] = [START_B]

  for (const character of value) {
    const point = character.charCodeAt(0)
    if (point < 32 || point > 126) {
      throw new Error('That code has a character Code 128 cannot carry.')
    }
    values.push(point - 32)
  }

  let checksum = START_B
  for (let index = 1; index < values.length; index++) {
    checksum += values[index]! * index
  }
  values.push(checksum % 103)

  return values.map((symbol) => CODE128[symbol]!).join('') + CODE128_STOP
}

/* --------------------------------------------------------------------- render */

export type Symbology = 'Ean13' | 'Code128'

export function encode(code: string, symbology: Symbology): string {
  return symbology === 'Ean13' ? encodeEan13(code) : encodeCode128(code)
}

/**
 * Draws a module pattern as SVG.
 *
 * Bars are drawn as a single path of rectangles at whole-module widths. Anything that resamples
 * them — a fractional width, a CSS transform — risks a scanner reading one module as two, so the
 * viewBox is in modules and the printed size is set by the enclosing element instead.
 */
export function barcodeSvg(
  code: string,
  symbology: Symbology,
  options: { heightModules?: number; showText?: boolean } = {},
): string {
  const modules = encode(code, symbology)
  const height = options.heightModules ?? 60
  const textHeight = options.showText === false ? 0 : 10
  const total = height + textHeight

  let bars = ''
  let index = 0
  while (index < modules.length) {
    if (modules[index] === '1') {
      let width = 1
      while (modules[index + width] === '1') width++
      bars += `<rect x="${index}" y="0" width="${width}" height="${height}"/>`
      index += width
    } else {
      index++
    }
  }

  const label =
    options.showText === false
      ? ''
      : `<text x="${modules.length / 2}" y="${total - 1}" text-anchor="middle"
           font-family="monospace" font-size="9" letter-spacing="0.5">${escapeText(code)}</text>`

  return `<svg viewBox="0 0 ${modules.length} ${total}" preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges"
    style="width:100%;height:100%">
    <rect width="${modules.length}" height="${total}" fill="#fff"/>
    <g fill="#000">${bars}</g>${label}</svg>`
}

function escapeText(value: string): string {
  return value.replace(/[&<>]/g, (character) =>
    character === '&' ? '&amp;' : character === '<' ? '&lt;' : '&gt;',
  )
}
